import { useEffect } from "react";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { OrderTemplateModal } from "./OrderTemplateModal";
import { useManageOrderTemplateViewModel } from "./useManageOrderTemplateViewModel";

export function ManageOrderTemplate() {
  const router = useRouter();

  const {
    orderTemplates,
    trades,
    isAddModalVisible,
    isDeleteModalVisible,
    isLoading,
    selectedOrderTemplate,
    loadOrderTemplates,
    handleAddOrderTemplate,
    toggleAddModal,
    openDeleteModal,
    onDelete,
    closeDeleteModal,
    getTrades,
  } = useManageOrderTemplateViewModel();

  const { hasPermission } = usePermissions();

  const theme = useTheme();

  useEffect(() => {
    loadOrderTemplates();
    getTrades();
  }, []);

  const orderFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: hasPermission(PERMISSIONS.ORDER_TEMPLATE.UPDATE)
        ? ({ row: { id, index }, value }) => (
            <MiLink
              key={id}
              title={value}
              to={`/settings/order-management/order-types/${id}/details`}
              data-testid={`order-template-link-${index}`}
            />
          )
        : null,
    },
    {
      headerName: "Abbr.",
      field: "abbreviation",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      headerName: "Color",
      field: "color",
      renderCell: ({ value }) => <input type="color" value={value} disabled />,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: openDeleteModal(row),
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          color={theme.palette.red.main}
        />
      ),
    },
  ];
  const customOnBack = () => {
    router.navigate(`/settings/order-management`);
  };
  return (
    <>
      <MiDetailPageHeader
        title={ENTITY_NAMES.ORDER_TEMPLATES}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        loading={isLoading}
        customOnBack={customOnBack}
        actionComponent={
          hasPermission(PERMISSIONS.ORDER_TEMPLATE.CREATE) && (
            <MiButton
              startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={toggleAddModal}
              data-testid="add-order-template"
              title={`Add ${ENTITY_NAMES.ORDER_TEMPLATE}`}
            />
          )
        }
      />
      <MiBox>
        <MiList
          columns={orderFieldset}
          data={orderTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={
            hasPermission(PERMISSIONS.ORDER_TEMPLATE.DELETE) && getRowActions
          }
          data-testid="order-templates-table"
        />
      </MiBox>
      <OrderTemplateModal
        open={isAddModalVisible}
        title={`Add ${ENTITY_NAMES.ORDER_TEMPLATE}`}
        trades={trades}
        buttonTitle={`Add ${ENTITY_NAMES.ORDER_TEMPLATE}`}
        onClose={toggleAddModal}
        onSubmit={handleAddOrderTemplate}
        loading={isLoading}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete: ${selectedOrderTemplate.name}?`}
        open={isDeleteModalVisible}
        handlePositive={onDelete}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={closeDeleteModal}
      />
    </>
  );
}
